"use client";

import { Box, BoxProps } from "@chakra-ui/react";

export type LineVariant = "solid" | "dashed" | "dot";

export interface CustomLineProps extends Omit<BoxProps, "children"> {
  variant?: LineVariant;
  color?: string;
  thickness?: number;
  width?: string | number | "auto";
  dashLength?: number;
  dashGap?: number;
  dotSize?: number;
  dotGap?: number;
}

export const CustomLine = ({
  variant = "solid",
  color = "neutral.300",
  thickness = 1,
  width = "auto",
  dashLength = 8,
  dashGap = 4,
  dotSize = 2,
  dotGap = 4,
  ...props
}: CustomLineProps) => {
  // Convert Chakra color to hex
  const getColorHex = (chakraColor: string) => {
    // Handle common Chakra colors
    const colorMap: Record<string, string> = {
      "neutral.100": "#E2E8F0",
      "neutral.300": "#CBD5E0",
      "neutral.200": "#E2E8F0",
      "neutral.400": "#A0AEC0",
      "neutral.500": "#718096",
      "primary.50": "#ECECFF",
      "primary.100": "#D4D5FF",
      "primary.200": "#A9AAFF",
      "primary.300": "#7E80FF",
      "primary.400": "#6265FF",
      "primary.500": "#5B60FD",
      "primary.600": "#494DEE",
      "primary.700": "#3B3FBE",
      "primary.800": "#2C2E8E",
      "primary.900": "#1D1E5F",
      "red.200": "#FEE2E2",
      "red.300": "#FECACA",
      "red.400": "#F87171",
      "red.500": "#EF4444",
      "red.600": "#DC2626",
      "red.700": "#B91C1C",
      "red.800": "#991B1B",
      "red.900": "#7F1D1D",
      "blue.50": "#EBF8FF",
      "blue.100": "#BEE3F8",
      "blue.200": "#90CDF4",
      "blue.300": "#63B3ED",
      "blue.400": "#4299E1",
      "blue.500": "#3182CE",
      "blue.600": "#2B6CB0",
      "green.50": "#F0FFF4",
      "green.100": "#C6F6D5",
      "green.200": "#9AE6B4",
      "green.300": "#68D391",
      "green.400": "#48BB78",
      "green.500": "#38A169",
      "green.600": "#258543",
    };
    if (colorMap[chakraColor]) {
      return colorMap[chakraColor];
    }
    if (chakraColor.startsWith("#")) {
      return chakraColor;
    }
    return "#CBD5E0";
  };

  const colorHex = getColorHex(color);

  const getLineStyles = () => {
    switch (variant) {
      case "dashed": {
        const dashArray = `${dashLength},${dashGap}`;
        const svgData = `data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3cline x1='0' y1='50%25' x2='100%25' y2='50%25' stroke='${encodeURIComponent(colorHex)}' stroke-width='${thickness}' stroke-dasharray='${dashArray}' stroke-linecap='butt'/%3e%3c/svg%3e`;

        return {
          height: `${thickness}px`,
          backgroundImage: `url("${svgData}")`,
          backgroundSize: "100% 100%",
          backgroundRepeat: "no-repeat",
        };
      }
      case "dot": {
        const dotArray = `${dotSize},${dotGap}`;
        const svgData = `data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3cline x1='0' y1='50%25' x2='100%25' y2='50%25' stroke='${encodeURIComponent(colorHex)}' stroke-width='${dotSize}' stroke-dasharray='${dotArray}' stroke-linecap='round'/%3e%3c/svg%3e`;

        return {
          height: `${thickness}px`,
          backgroundImage: `url("${svgData}")`,
          backgroundSize: "100% 100%",
          backgroundRepeat: "no-repeat",
        };
      }
      case "solid":
      default:
        return {
          height: `${thickness}px`,
          backgroundColor: color,
        };
    }
  };

  return (
    <Box
      width={width === "auto" ? "100%" : width}
      {...getLineStyles()}
      {...props}
    />
  );
};
